/**
 * Modal viewer for a TCG card shared through chat.
 *
 * The server supplies only a validated concrete card identity.
 * This module resolves the canonical definition and reuses the shared card-face
 * renderer, so the Binder, battle UI, pack reveal, and chat viewer stay identical.
 */
import { cardDef, type CardRarity } from './sim/index';
import type { WireCardLink } from './net/protocol';
import { cardFaceModel, renderCardFace } from './cardFace';
import { createPanelButton, createPromptCard } from './ui/prompt';

export interface CardPreviewDeps {
  doc?: Document;
}

export interface CardPreviewHandle {
  open(card: WireCardLink): void;
  close(): void;
  isOpen(): boolean;
}

export function initCardPreview(deps: CardPreviewDeps = {}): CardPreviewHandle {
  const doc = deps.doc ?? document;
  const noop: CardPreviewHandle = {
    open: () => {},
    close: () => {},
    isOpen: () => false,
  };
  if (!doc.body) return noop;

  const prompt = createPromptCard(doc);
  prompt.root.id = 'card-preview';
  prompt.root.setAttribute('aria-label', 'Shared card');
  prompt.root.style.minWidth = '0';
  prompt.root.style.width = 'min(280px, calc(100vw - 48px))';
  prompt.root.style.maxWidth = '280px';

  const title = doc.createElement('h2');
  title.id = 'card-preview-title';
  Object.assign(title.style, {
    margin: '0 0 12px',
    color: '#d8c690',
    font: "600 17px/1.2 Georgia, 'Times New Roman', serif",
    letterSpacing: '0.4px',
  } as Partial<CSSStyleDeclaration>);
  prompt.root.setAttribute('aria-labelledby', title.id);

  const stage = doc.createElement('div');
  stage.id = 'card-preview-stage';
  Object.assign(stage.style, {
    width: '220px',
    maxWidth: '100%',
    margin: '0 auto 16px',
  } as Partial<CSSStyleDeclaration>);

  const closeButton = createPanelButton(doc, 'Close', 'plain');
  closeButton.id = 'card-preview-close';
  prompt.root.append(title, stage, closeButton);
  let returnFocus: HTMLElement | null = null;

  const close = (): void => {
    if (!prompt.isOpen()) return;
    prompt.hide();
    stage.replaceChildren();
    const target = returnFocus;
    returnFocus = null;
    if (target?.isConnected) target.focus();
  };

  closeButton.addEventListener('click', close);
  doc.addEventListener('keydown', (event) => {
    if (!prompt.isOpen()) return;
    if (event.key !== 'Escape' && event.key !== 'Tab') return;
    event.stopImmediatePropagation();
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }
    const focusable = Array.from(
      prompt.root.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), ' +
        'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ).filter((element) => !element.hidden);
    const first = focusable[0];
    const last = focusable.at(-1);
    if (!first || !last) {
      event.preventDefault();
      prompt.root.focus();
      return;
    }
    const active = doc.activeElement;
    if (event.shiftKey && (active === first || !prompt.root.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !prompt.root.contains(active))) {
      event.preventDefault();
      first.focus();
    }
  }, true);

  return {
    open(card) {
      let def;
      try {
        def = cardDef(card.cardId, card.rarity as CardRarity);
      } catch {
        return;
      }
      title.textContent = `${capitalize(def.rarity)} ${def.name}`;
      stage.replaceChildren(renderCardFace(doc, cardFaceModel(def)));
      if (!prompt.isOpen()) {
        const active = doc.activeElement as HTMLElement | null;
        returnFocus = active && typeof active.focus === 'function' ? active : null;
      }
      prompt.show();
      closeButton.focus();
    },
    close,
    isOpen: prompt.isOpen,
  };
}

function capitalize(value: string): string {
  return value.length === 0 ? value : value[0]!.toUpperCase() + value.slice(1);
}
